import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])';

const VideoModal = ({ isOpen, onClose, videoId, title, triggerRef, isShort = false }) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousActive = document.activeElement;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.body.style.overflow = 'unset';
      const target = triggerRef?.current || previousActive;
      if (target && typeof target.focus === 'function') target.focus();
    };
  }, [isOpen, triggerRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = dialogRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const titleId = 'video-modal-title';
  // Shorts use playsinline=1 so iOS keeps the video embedded rather than
  // launching fullscreen. The click on the thumbnail counts as a user gesture,
  // so desktop browsers allow unmuted autoplay; iOS may still block it and
  // require a second tap to start audio.
  const embedParams = isShort
    ? 'autoplay=1&rel=0&modestbranding=1&playsinline=1'
    : 'autoplay=1&rel=0&modestbranding=1';
  const embedUrl = `https://www.youtube.com/embed/${videoId}?${embedParams}`;

  const containerClass = isShort
    ? 'relative w-[95vw] max-w-[360px]'
    : 'relative w-full max-w-[720px]';
  const aspectClass = isShort ? 'aspect-[9/16]' : 'aspect-video';

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={containerClass}
      >
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-12 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Icon name="X" size={20} />
        </button>
        <h2 id={titleId} className="sr-only">{title}</h2>
        <div className={`${aspectClass} w-full overflow-hidden rounded-xl bg-black shadow-2xl`}>
          <iframe
            src={embedUrl}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full border-0"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VideoModal;
